/* ==============================================================
   HMD VAULT v2 — native data plane.
   One baked JSON (v3-vault-data.json) → native SVG charts.
   No iframes, no third-party scripts, no tokens in the page.
   ============================================================== */
(function () {
  'use strict';
  var $  = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var SESSION_KEY = 'hmd_vault_unlocked_v1';
  var COSTS_KEY   = 'hmd_vault_costs_v1';
  var DATA = null;             // baked payload
  // Matches the 'HMD Vault Auto-Bake' scheduled task. The Refresh button can
  // only ever hand back the newest BAKE — it cannot recompute the numbers —
  // so the UI has to be honest about where the next one is.
  var BAKE_EVERY_MIN = 5;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Card registry: which widgets render on which panel ───── */
  var REGISTRY = {
    today:     [ { id: 'rev-30d', span: 2 }, { id: 'live-visitors', span: 2 }, { id: 'email-capture', span: 2 } ],
    store:     [ { id: 'store-rev-weekly', span: 2 }, { id: 'store-aov-weekly', span: 2 },
                 { id: 'store-top-products', span: 2 }, { id: 'store-split' }, { id: 'store-discounts' },
                 { id: 'store-inventory', span: 2 }, { id: 'store-recent-orders', span: 2 } ],
    email:     [ { id: '__pnl', span: 2 }, { id: 'email-klaviyo-campaigns', span: 2 },
                 { id: 'email-deliverability', span: 2 }, { id: 'email-klaviyo-flows', span: 2 },
                 { id: 'email-lists', span: 2 }, { id: 'email-rev-split', span: 2 },
                 { id: 'email-orders', span: 2 }, { id: '__klaviyo', span: 2 } ],
    quiz:      [ { id: 'quiz-completions-trend' }, { id: 'quiz-median-time' }, { id: 'quiz-top-concerns', span: 2 },
                 { id: 'quiz-gender' }, { id: 'quiz-age' }, { id: 'quiz-risk' }, { id: 'quiz-device' }, { id: 'quiz-step-time', span: 2 } ],
    audience:  [ { id: 'audience-dau', span: 2 }, { id: 'audience-wau', span: 2 },
                 { id: 'audience-newreturning', span: 2 }, { id: 'audience-growth', span: 2 }, { id: 'audience-retention', span: 2 } ],
    funnel:    [ { id: 'funnel-dropoff', span: 2 }, { id: 'funnel-quizstart', span: 2 } ],
    traffic:   [ { id: 'traffic-sources', span: 2 }, { id: 'traffic-referrers', span: 2 }, { id: 'traffic-sessions', span: 2 } ],
    behavior:  [ { id: 'behavior-browser-share', span: 2 }, { id: 'behavior-browser', span: 2 } ],
    geography: [ { id: 'geo-shopify-rev', span: 2 }, { id: 'geo-shopify-orders', span: 2 }, { id: 'geo-countries', span: 2 } ],
    system:    [ { id: '__health', span: 2 }, { id: '__sysinfo', span: 2 } ]
  };

  /* ── utils ────────────────────────────────────────────────── */
  function sha256(str) {
    var buf = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', buf).then(function (h) {
      return Array.prototype.map.call(new Uint8Array(h), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    });
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }
  function fmt(v) {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') {
      if (Math.abs(v) >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
      return (Math.round(v * 10) / 10).toString();
    }
    return String(v);
  }
  function money(v) { return '$' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }); }
  function announce(msg) { var s = $('#vault-status'); if (s) s.textContent = msg; }
  function timeAgo(iso) {
    var ms = Date.now() - new Date(iso).getTime();
    var m = Math.floor(ms / 60000);
    if (m < 1)   return 'just now';
    if (m < 60)  return m + 'm ago';
    var h = Math.floor(m / 60);
    if (h < 48)  return h + 'h ago';
    return Math.floor(h / 24) + 'd ago';
  }

  /* ── SVG helpers ──────────────────────────────────────────── */
  var NS = 'http://www.w3.org/2000/svg';
  function svgEl(tag, attrs) {
    var n = document.createElementNS(NS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }

  /* ── renderers ────────────────────────────────────────────── */
  function renderBignum(mount, w) {
    var box = el('div', 'w-bignum');
    var v = el('div', 'w-bignum-value', fmt(w.value));
    if (w.unit) v.appendChild(el('span', 'w-bignum-unit', ' ' + w.unit));
    box.appendChild(v);
    if (w.sub) box.appendChild(el('div', 'w-bignum-sub', w.sub));
    mount.appendChild(box);
  }

  function renderArea(mount, w) {
    var s = (w.series && w.series[0]) || { data: [] };
    var data = s.data || [];
    var labels = w.labels || [];
    if (!data.length) return renderEmpty(mount, w);
    var W = 560, H = 180, P = { t: 12, r: 8, b: 22, l: 34 };
    var iw = W - P.l - P.r, ih = H - P.t - P.b;
    var max = Math.max.apply(null, data.concat([1]));
    var x = function (i) { return P.l + (data.length === 1 ? iw / 2 : i * iw / (data.length - 1)); };
    var y = function (v) { return P.t + ih - (v / max) * ih; };
    var svg = svgEl('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'chart chart-area', role: 'img',
                             'aria-label': w.title + ', peak ' + fmt(max) });
    // gridlines + y labels
    [0, 0.5, 1].forEach(function (t) {
      var gy = P.t + ih - t * ih;
      svg.appendChild(svgEl('line', { x1: P.l, x2: W - P.r, y1: gy, y2: gy, class: 'gline' }));
      var lbl = svgEl('text', { x: P.l - 6, y: gy + 3, class: 'axis', 'text-anchor': 'end' });
      lbl.textContent = fmt(Math.round(max * t));
      svg.appendChild(lbl);
    });
    // x labels: first / mid / last
    [0, Math.floor((data.length - 1) / 2), data.length - 1].forEach(function (i) {
      if (!labels[i]) return;
      var lbl = svgEl('text', { x: x(i), y: H - 6, class: 'axis', 'text-anchor': i === 0 ? 'start' : (i === data.length - 1 ? 'end' : 'middle') });
      lbl.textContent = labels[i];
      svg.appendChild(lbl);
    });
    var line = data.map(function (v, i) { return (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1); }).join(' ');
    var area = line + ' L' + x(data.length - 1).toFixed(1) + ' ' + (P.t + ih) + ' L' + x(0).toFixed(1) + ' ' + (P.t + ih) + ' Z';
    var gid = 'g' + Math.random().toString(36).slice(2, 8);
    var defs = svgEl('defs', {});
    var grad = svgEl('linearGradient', { id: gid, x1: 0, y1: 0, x2: 0, y2: 1 });
    grad.appendChild(svgEl('stop', { offset: '0%', class: 'grad-top' }));
    grad.appendChild(svgEl('stop', { offset: '100%', class: 'grad-bot' }));
    defs.appendChild(grad); svg.appendChild(defs);
    svg.appendChild(svgEl('path', { d: area, fill: 'url(#' + gid + ')', class: 'area' }));
    svg.appendChild(svgEl('path', { d: line, class: 'line' }));
    // endpoint dot
    svg.appendChild(svgEl('circle', { cx: x(data.length - 1), cy: y(data[data.length - 1]), r: 3.2, class: 'dot' }));
    // hover
    var hover = svgEl('circle', { r: 3.2, class: 'dot dot-hover', opacity: 0 });
    svg.appendChild(hover);
    svg.addEventListener('pointermove', function (e) {
      var rect = svg.getBoundingClientRect();
      var px = (e.clientX - rect.left) / rect.width * W;
      var i = Math.max(0, Math.min(data.length - 1, Math.round((px - P.l) / (iw / Math.max(1, data.length - 1)))));
      hover.setAttribute('cx', x(i)); hover.setAttribute('cy', y(data[i])); hover.setAttribute('opacity', 1);
      showTip(e, (labels[i] ? labels[i] + ' · ' : '') + (w.unit === '$' ? money(data[i]) : fmt(data[i])));
    });
    svg.addEventListener('pointerleave', function () { hover.setAttribute('opacity', 0); hideTip(); });
    mount.appendChild(svg);
    if (w.sub) mount.appendChild(el('div', 'w-sub', w.sub));
  }

  function renderHbars(mount, w) {
    var rows = w.rows || [];
    if (!rows.length) return renderEmpty(mount, w);
    var max = Math.max.apply(null, rows.map(function (r) { return r.value; }).concat([1]));
    var box = el('div', 'w-hbars');
    rows.forEach(function (r, i) {
      var row = el('div', 'hbar');
      row.appendChild(el('span', 'hbar-label', r.label || '—'));
      var track = el('div', 'hbar-track');
      var fill = el('div', 'hbar-fill');
      fill.style.width = Math.max(2, r.value / max * 100) + '%';
      if (!reduceMotion) fill.style.transitionDelay = (i * 40) + 'ms';
      track.appendChild(fill);
      row.appendChild(track);
      row.appendChild(el('span', 'hbar-value', w.unit === '$' ? money(r.value) : fmt(r.value)));
      box.appendChild(row);
    });
    mount.appendChild(box);
    if (w.sub) mount.appendChild(el('div', 'w-sub', w.sub));
  }

  function renderFunnel(mount, w) {
    var steps = w.steps || [];
    if (!steps.length || !steps[0].count) return renderEmpty(mount, w);
    var first = steps[0].count || 1;
    var box = el('div', 'w-funnel');
    steps.forEach(function (s, i) {
      var row = el('div', 'fstep');
      var head = el('div', 'fstep-head');
      head.appendChild(el('span', 'fstep-label', (i + 1) + '. ' + s.label));
      var pct = Math.round(100 * s.count / first);
      head.appendChild(el('span', 'fstep-nums', fmt(s.count) + ' · ' + pct + '%'));
      row.appendChild(head);
      var track = el('div', 'fstep-track');
      var fill = el('div', 'fstep-fill');
      fill.style.width = Math.max(2, pct) + '%';
      if (!reduceMotion) fill.style.transitionDelay = (i * 60) + 'ms';
      track.appendChild(fill);
      row.appendChild(track);
      if (i > 0) {
        var prev = steps[i - 1].count || 1;
        row.appendChild(el('div', 'fstep-conv', Math.round(100 * s.count / prev) + '% from previous step'));
      }
      box.appendChild(row);
    });
    mount.appendChild(box);
    if (w.sub) mount.appendChild(el('div', 'w-sub', w.sub));
  }

  function renderLifecycle(mount, w) {
    var series = w.series || [];
    if (!series.length) return renderEmpty(mount, w);
    var n = (series[0].data || []).length;
    if (!n) return renderEmpty(mount, w);
    var W = 560, H = 190, P = { t: 12, r: 8, b: 34, l: 34 };
    var iw = W - P.l - P.r, ih = H - P.t - P.b;
    var maxPos = 1, maxNeg = 0;
    for (var i = 0; i < n; i++) {
      var pos = 0, neg = 0;
      series.forEach(function (s) { var v = (s.data || [])[i] || 0; if (v >= 0) pos += v; else neg += -v; });
      maxPos = Math.max(maxPos, pos); maxNeg = Math.max(maxNeg, neg);
    }
    var total = maxPos + maxNeg;
    var zeroY = P.t + (maxPos / total) * ih;
    var bw = Math.max(2, iw / n - 2);
    var svg = svgEl('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'chart chart-life', role: 'img', 'aria-label': w.title });
    svg.appendChild(svgEl('line', { x1: P.l, x2: W - P.r, y1: zeroY, y2: zeroY, class: 'gline gline-zero' }));
    for (i = 0; i < n; i++) {
      var upY = zeroY, dnY = zeroY;
      var bx = P.l + i * (iw / n) + 1;
      for (var si = 0; si < series.length; si++) {
        var v = (series[si].data || [])[i] || 0;
        if (!v) continue;
        var hpx = Math.abs(v) / total * ih;
        var rect = svgEl('rect', { x: bx, width: bw, height: Math.max(1, hpx), class: 'life life-' + si,
                                   y: v >= 0 ? (upY - hpx) : dnY, rx: 1 });
        var tipTxt = (w.labels && w.labels[i] ? w.labels[i] + ' · ' : '') + (series[si].label || '') + ': ' + fmt(v);
        (function (t) {
          rect.addEventListener('pointermove', function (e) { showTip(e, t); });
          rect.addEventListener('pointerleave', hideTip);
        })(tipTxt);
        svg.appendChild(rect);
        if (v >= 0) upY -= hpx; else dnY += hpx;
      }
    }
    mount.appendChild(svg);
    var legend = el('div', 'w-legend');
    series.forEach(function (s, si) {
      var it = el('span', 'legend-item');
      it.appendChild(el('i', 'legend-swatch life-' + si));
      it.appendChild(el('span', null, s.label || ('Series ' + (si + 1))));
      legend.appendChild(it);
    });
    mount.appendChild(legend);
  }

  function renderRetention(mount, w) {
    var cohorts = w.cohorts || [];
    if (!cohorts.length) return renderEmpty(mount, w);
    var box = el('div', 'w-retention');
    var maxLen = Math.max.apply(null, cohorts.map(function (c) { return (c.values || []).length; }));
    var head = el('div', 'ret-row ret-head');
    head.appendChild(el('span', 'ret-label', 'Cohort'));
    for (var p = 0; p < maxLen; p++) head.appendChild(el('span', 'ret-cell ret-cell-h', 'W' + p));
    box.appendChild(head);
    cohorts.forEach(function (c) {
      var row = el('div', 'ret-row');
      row.appendChild(el('span', 'ret-label', c.label || ''));
      var base = (c.values || [])[0] || 0;
      (c.values || []).forEach(function (v) {
        var cell = el('span', 'ret-cell', base ? Math.round(100 * v / base) + '%' : '—');
        var a = base ? Math.max(0.06, v / base) : 0.04;
        cell.style.setProperty('--cell-a', a.toFixed(2));
        row.appendChild(cell);
      });
      box.appendChild(row);
    });
    mount.appendChild(box);
    if (w.sub) mount.appendChild(el('div', 'w-sub', w.sub));
  }

  function renderTable(mount, w) {
    var rows = w.rows || [];
    if (!rows.length) return renderEmpty(mount, w);
    var wrap = el('div', 'w-tablewrap');
    var table = el('table', 'w-table');
    if (w.cols && w.cols.length) {
      var thead = el('thead'); var tr = el('tr');
      w.cols.forEach(function (c) { tr.appendChild(el('th', null, c)); });
      thead.appendChild(tr); table.appendChild(thead);
    }
    var tbody = el('tbody');
    rows.forEach(function (r) {
      var tr2 = el('tr');
      r.forEach(function (cell) { tr2.appendChild(el('td', null, String(cell))); });
      tbody.appendChild(tr2);
    });
    table.appendChild(tbody); wrap.appendChild(table); mount.appendChild(wrap);
  }

  function renderEmpty(mount, w) {
    var box = el('div', 'w-empty');
    box.appendChild(el('div', 'w-empty-mark', '∅'));
    box.appendChild(el('div', 'w-empty-title', 'No data yet'));
    box.appendChild(el('div', 'w-empty-msg', (w && (w.note || w.sub)) || 'Nothing recorded in this window.'));
    mount.appendChild(box);
  }

  var RENDERERS = { bignum: renderBignum, area: renderArea, line: renderArea, hbars: renderHbars,
                    funnel: renderFunnel, lifecycle: renderLifecycle, retention: renderRetention, table: renderTable };

  /* ── detail modal (expand any card) ───────────────────────── */
  function detailRows(w) {
    if (w.type === 'table') return { cols: w.cols || [], rows: w.rows || [] };
    if (w.type === 'hbars') return { cols: ['Item', 'Value'], rows: (w.rows || []).map(function (r) {
      return [r.label, w.unit === '$' ? money(r.value) : fmt(r.value)]; }) };
    if (w.type === 'funnel') {
      var first = (w.steps && w.steps[0] && w.steps[0].count) || 1;
      return { cols: ['Step', 'Count', '% of start'], rows: (w.steps || []).map(function (s) {
        return [s.label, fmt(s.count), Math.round(100 * s.count / first) + '%']; }) };
    }
    if (w.type === 'area' || w.type === 'line') {
      var s = (w.series && w.series[0]) || { data: [] };
      return { cols: ['When', 'Value'], rows: (s.data || []).map(function (v, i) {
        return [(w.labels || [])[i] || (i + 1), w.unit === '$' ? money(v) : fmt(v)]; }).reverse() };
    }
    if (w.type === 'lifecycle') {
      var cols = ['When'].concat((w.series || []).map(function (sr) { return sr.label; }));
      var base = ((w.series || [])[0] || {}).data || [];
      return { cols: cols, rows: base.map(function (_, i) {
        return [(w.labels || [])[i] || (i + 1)].concat((w.series || []).map(function (sr) { return fmt((sr.data || [])[i] || 0); }));
      }).reverse() };
    }
    if (w.type === 'retention') {
      var maxLen = Math.max.apply(null, (w.cohorts || []).map(function (c) { return (c.values || []).length; }).concat([0]));
      var cols2 = ['Cohort']; for (var p = 0; p < maxLen; p++) cols2.push('W' + p);
      return { cols: cols2, rows: (w.cohorts || []).map(function (c) { return [c.label].concat((c.values || []).map(fmt)); }) };
    }
    return null;
  }
  function openDetail(w) {
    closeDetail();
    var overlay = el('div', 'dmodal');
    overlay.setAttribute('data-dmodal', '');
    var card = el('div', 'dmodal-card');
    card.setAttribute('role', 'dialog'); card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', w.title || 'Detail');
    var head = el('header', 'dmodal-head');
    head.appendChild(el('h2', 'dmodal-title', w.title || ''));
    var close = el('button', 'dmodal-close', '✕');
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', closeDetail);
    head.appendChild(close);
    card.appendChild(head);
    if (w.desc) card.appendChild(el('p', 'dmodal-desc', w.desc));
    var mount = el('div', 'dmodal-chart');
    card.appendChild(mount);
    try {
      if (w.empty) renderEmpty(mount, w);
      else (RENDERERS[w.type] || renderEmpty)(mount, w);
    } catch (e) { renderEmpty(mount, { note: 'Render error' }); }
    var det = !w.empty && w.type !== 'table' && detailRows(w);
    if (det && det.rows && det.rows.length) {
      card.appendChild(el('div', 'dmodal-sub', 'Every data point'));
      renderTable(card, { rows: det.rows, cols: det.cols });
    }
    overlay.appendChild(card);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeDetail(); });
    document.body.appendChild(overlay);
    document.body.classList.add('has-dmodal');
    close.focus();
  }
  function closeDetail() {
    var m = $('[data-dmodal]');
    if (m) m.remove();
    document.body.classList.remove('has-dmodal');
  }

  /* ── tooltip ──────────────────────────────────────────────── */
  var tipEl;
  function showTip(e, text) {
    if (!tipEl) tipEl = $('[data-tooltip]');
    if (!tipEl) return;
    tipEl.textContent = text;
    tipEl.hidden = false;
    var pad = 14;
    var x = Math.min(e.clientX + pad, window.innerWidth - tipEl.offsetWidth - 8);
    var y = Math.max(8, e.clientY - tipEl.offsetHeight - 10);
    tipEl.style.transform = 'translate(' + x + 'px,' + y + 'px)';
  }
  function hideTip() { if (tipEl) tipEl.hidden = true; }

  /* ── special cards (Email P&L, Klaviyo, System) ───────────── */
  function getCosts() {
    try { return JSON.parse(localStorage.getItem(COSTS_KEY)) || {}; } catch (e) { return {}; }
  }
  function setCosts(c) { try { localStorage.setItem(COSTS_KEY, JSON.stringify(c)); } catch (e) {} }

  function renderPnl(mount) {
    var em = (DATA && DATA.email) || {};
    var costs = getCosts();
    var box = el('div', 'pnl');

    var intro = el('p', 'pnl-intro');
    intro.textContent = em.klaviyo_connected
      ? 'Money in from email (exact Klaviyo attribution) vs. money out. Enter your monthly costs once — they persist on this device.'
      : 'Money in from email vs. money out. Revenue uses Shopify-side email signals; enter your monthly costs once — they persist on this device.';
    box.appendChild(intro);

    var grid = el('div', 'pnl-inputs');
    var fields = [
      { key: 'klaviyoCost',  label: 'Klaviyo plan / mo' },
      { key: 'agencyCost',   label: "Email team (Kush) / mo" },
      { key: 'otherCost',    label: 'Other email costs / mo' },
      { key: 'manualRevenue', label: 'Override revenue / 30d (optional)' }
    ];
    fields.forEach(function (f) {
      var lab = el('label', 'pnl-field');
      lab.appendChild(el('span', 'pnl-field-label', f.label));
      var input = el('input', 'pnl-input');
      input.type = 'number'; input.min = '0'; input.step = '1'; input.placeholder = '0';
      if (costs[f.key] !== undefined && costs[f.key] !== '') input.value = costs[f.key];
      input.addEventListener('input', function () {
        var c = getCosts();
        c[f.key] = input.value === '' ? '' : Number(input.value);
        setCosts(c); paintPnlNumbers(box);
      });
      lab.appendChild(input);
      grid.appendChild(lab);
    });
    box.appendChild(grid);
    box.appendChild(el('div', 'pnl-numbers'));
    mount.appendChild(box);
    paintPnlNumbers(box);
  }

  function paintPnlNumbers(box) {
    var em = (DATA && DATA.email) || {};
    var c = getCosts();
    var costTotal = (Number(c.klaviyoCost) || 0) + (Number(c.agencyCost) || 0) + (Number(c.otherCost) || 0);
    var attributed = (em.attributed_30d && em.attributed_30d.revenue) || 0;
    var klav = (em.klaviyo_30d && em.klaviyo_30d.revenue) || 0;
    var revenue, srcLabel;
    if ((Number(c.manualRevenue) || 0) > 0) { revenue = Number(c.manualRevenue); srcLabel = 'manual override'; }
    else if (em.klaviyo_connected) { revenue = klav; srcLabel = 'Klaviyo API'; }
    else { revenue = attributed; srcLabel = 'Shopify signals'; }
    var net = revenue - costTotal;
    var roi = costTotal > 0 ? Math.round(100 * net / costTotal) : null;
    var numbers = $('.pnl-numbers', box);
    numbers.textContent = '';
    var items = [
      { label: 'Email revenue · 30d', value: money(revenue), note: srcLabel },
      { label: 'Costs · monthly', value: money(costTotal) },
      { label: 'Net', value: money(net), cls: net >= 0 ? 'is-good' : 'is-bad' },
      { label: 'ROI', value: roi === null ? '—' : roi + '%', cls: roi !== null ? (roi >= 0 ? 'is-good' : 'is-bad') : '' }
    ];
    items.forEach(function (it) {
      var cell = el('div', 'pnl-num ' + (it.cls || ''));
      cell.appendChild(el('div', 'pnl-num-value', it.value));
      cell.appendChild(el('div', 'pnl-num-label', it.label + (it.note ? ' · ' + it.note : '')));
      numbers.appendChild(cell);
    });
  }

  function renderKlaviyoCard(mount) {
    var em = (DATA && DATA.email) || {};
    var box = el('div', 'note-card');
    box.appendChild(el('div', 'note-title', em.klaviyo_connected ? 'Klaviyo connected' : 'Exact numbers need Klaviyo'));
    var p = el('p', 'note-body');
    p.textContent = em.klaviyo_connected
      ? 'Exact attributed revenue is flowing from the Klaviyo API (read-only key). Campaign and flow tables above are the money-per-email truth; the Shopify-signals split below is kept as a cross-check.'
      : 'Shopify only sees email orders that click a tagged link — renewals and untagged sends are invisible. ' +
        'A read-only Klaviyo Private API key unlocks exact attributed revenue per campaign and flow. ' +
        'Until then, type the attributed-revenue number from Klaviyo’s dashboard into the field on the left.';
    box.appendChild(p);
    var stats = el('div', 'note-stats');
    var lastPair = em.klaviyo_connected
      ? ['Email (Klaviyo)', money((em.klaviyo_30d || {}).revenue || 0)]
      : ['AOV', money((em.store_30d || {}).aov || 0)];
    [['Store rev · 30d', money((em.store_30d || {}).revenue || 0)],
     ['Subscriptions', money((em.subs_30d || {}).revenue || 0)],
     ['Web checkout', money((em.web_30d || {}).revenue || 0)],
     lastPair].forEach(function (pair) {
      var sEl = el('div', 'note-stat');
      sEl.appendChild(el('div', 'note-stat-v', pair[1]));
      sEl.appendChild(el('div', 'note-stat-l', pair[0]));
      stats.appendChild(sEl);
    });
    box.appendChild(stats);
    mount.appendChild(box);
  }

  function renderHealth(mount) {
    var health = (DATA && DATA.health) || [];
    var box = el('div', 'health');
    if (!health.length) {
      box.appendChild(el('div', 'health-ok', 'No data-health alerts. All sources reporting.'));
    }
    health.forEach(function (h) {
      var row = el('div', 'health-row health-' + (h.level || 'amber'));
      row.appendChild(el('span', 'health-dot'));
      row.appendChild(el('span', 'health-msg', h.msg));
      box.appendChild(row);
    });
    mount.appendChild(box);
  }

  function renderSysinfo(mount) {
    var meta = (DATA && DATA.meta) || {};
    var src = meta.sources || {};
    var box = el('div', 'sysinfo');
    var rows = [
      ['Data baked', meta.baked_at ? new Date(meta.baked_at).toLocaleString() : '—'],
      ['PostHog insights', (src.posthog_ok || 0) + ' / ' + (src.posthog_insights || 0) + ' fetched'],
      ['Shopify orders scanned', (src.shopify_orders_90d || 0) + ' (90d)'],
      ['Widgets rendered', Object.keys((DATA && DATA.widgets) || {}).length],
      ['Auto-bake', 'Every 15 minutes on the operator PC (Task Scheduler: "HMD Vault Auto-Bake"). This page re-checks every 2 minutes and on tab focus — no button needed.']
    ];
    rows.forEach(function (r) {
      var row = el('div', 'sys-row');
      row.appendChild(el('span', 'sys-k', r[0]));
      row.appendChild(el('span', 'sys-v', String(r[1])));
      box.appendChild(row);
    });
    var links = el('div', 'sys-links');
    [['PostHog', 'https://us.posthog.com/project/453570'],
     ['Shopify admin', 'https://admin.shopify.com/store/utejch-ba']].forEach(function (pair) {
      var a = el('a', 'btn btn-ghost', pair[0]);
      a.href = pair[1]; a.target = '_blank'; a.rel = 'noopener';
      links.appendChild(a);
    });
    box.appendChild(links);
    mount.appendChild(box);
  }

  var SPECIALS = { '__pnl': { title: 'Email P&L', render: renderPnl },
                   '__klaviyo': { title: 'Context', render: renderKlaviyoCard },
                   '__health': { title: 'Data health', render: renderHealth },
                   '__sysinfo': { title: 'Pipeline', render: renderSysinfo } };

  /* ── panel builder ────────────────────────────────────────── */
  function buildPanel(slug) {
    var panel = $('[data-panel="' + slug + '"]');
    if (!panel || panel.dataset.built) return;
    panel.dataset.built = '1';
    // KPI ribbon
    var kpis = (DATA.kpis || {})[slug] || [];
    if (kpis.length) {
      var ribbon = el('div', 'kpis');
      kpis.forEach(function (k) {
        var tile = el('div', 'kpi');
        var v = el('div', 'kpi-value', fmt(k.value));
        if (k.delta !== undefined && k.delta !== null && k.delta !== 0) {
          v.appendChild(el('span', 'kpi-delta ' + (k.delta > 0 ? 'is-good' : 'is-bad'),
                           (k.delta > 0 ? '+' : '') + fmt(k.delta)));
        }
        tile.appendChild(v);
        tile.appendChild(el('div', 'kpi-label', k.label));
        ribbon.appendChild(tile);
      });
      panel.appendChild(ribbon);
    }
    // widget grid
    var grid = el('div', 'grid');
    (REGISTRY[slug] || []).forEach(function (slot) {
      var card = el('article', 'card' + (slot.span === 2 ? ' card-wide' : ''));
      var special = SPECIALS[slot.id];
      var w = special ? { title: special.title } : (DATA.widgets || {})[slot.id];
      if (!w) return;
      var head = el('header', 'card-head');
      head.appendChild(el('h2', 'card-title', w.title || slot.id));
      if (!special) {
        var ex = el('button', 'card-expand', '⤢');
        ex.setAttribute('aria-label', 'Expand ' + (w.title || 'card'));
        ex.title = 'Expand — bigger chart, plain-English note, every data point';
        (function (wRef) { ex.addEventListener('click', function () { openDetail(wRef); }); })(w);
        head.appendChild(ex);
      }
      if (w.desc) card.title = w.desc;
      card.appendChild(head);
      var mount = el('div', 'card-body');
      card.appendChild(mount);
      try {
        if (special) special.render(mount);
        else if (w.empty) renderEmpty(mount, w);
        else (RENDERERS[w.type] || renderEmpty)(mount, w);
      } catch (err) {
        renderEmpty(mount, { note: 'Render error' });
      }
      grid.appendChild(card);
    });
    panel.appendChild(grid);
  }

  /* ── tabs / router ────────────────────────────────────────── */
  var current = 'today';
  function activate(slug, pushHash) {
    if (!REGISTRY[slug]) slug = 'today';
    current = slug;
    $$('[role="tab"]').forEach(function (t) {
      var on = t.dataset.tab === slug;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    $$('[data-panel]').forEach(function (p) { p.hidden = p.dataset.panel !== slug; });
    $$('[data-bottomnav] button').forEach(function (b) {
      b.classList.toggle('is-active', b.dataset.nav === slug);
    });
    if (DATA) buildPanel(slug);
    if (pushHash !== false) { try { history.replaceState(null, '', '#tab=' + slug); } catch (e) {} }
    announce(slug + ' tab');
  }

  function wireTabs() {
    $$('[role="tab"]').forEach(function (t) {
      t.addEventListener('click', function () { activate(t.dataset.tab); });
    });
    var strip = $('[data-tabstrip]');
    if (strip) strip.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      var tabs = $$('[role="tab"]', strip);
      var i = tabs.findIndex(function (t) { return t.dataset.tab === current; });
      var next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      next.focus(); activate(next.dataset.tab);
    });
    $$('[data-bottomnav] button, [data-moresheet] button').forEach(function (b) {
      if (!b.dataset.nav) return;
      b.addEventListener('click', function () {
        if (b.dataset.nav === '__more') return toggleMore(true);
        toggleMore(false);
        activate(b.dataset.nav);
      });
    });
    var moreBtn = $('[data-more-btn]');
    if (moreBtn) moreBtn.addEventListener('click', function () { toggleMore(true); });
    var moreClose = $('[data-more-close]');
    if (moreClose) moreClose.addEventListener('click', function () { toggleMore(false); });
    var sheet = $('[data-moresheet]');
    if (sheet) sheet.addEventListener('click', function (e) { if (e.target === sheet) toggleMore(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { toggleMore(false); closeDetail(); }
      if (e.target && /input|textarea/i.test(e.target.tagName)) return;
      if (e.key === 'r' && !e.metaKey && !e.ctrlKey) refreshData(true);
      var n = parseInt(e.key, 10);
      var order = Object.keys(REGISTRY);
      if (n >= 1 && n <= order.length) activate(order[n - 1]);
    });
  }
  function toggleMore(open) {
    var sheet = $('[data-moresheet]');
    if (sheet) sheet.hidden = !open;
  }

  /* ── data load / freshness ────────────────────────────────── */
  function paintSync() {
    var label = $('[data-sync-label]');
    var dot = $('[data-sync-dot]');
    if (!label || !DATA || !DATA.meta) return;
    var baked = DATA.meta.baked_at;
    label.textContent = timeAgo(baked);
    var mins = (Date.now() - new Date(baked).getTime()) / 60000;
    dot.className = 'sync-dot ' + (mins < 12 ? 'is-fresh' : mins < 40 ? 'is-aging' : 'is-stale');
    label.title = 'Baked at ' + new Date(baked).toLocaleString() + ' · auto-bakes every ' + BAKE_EVERY_MIN + ' min';
  }
  function flashBtn(btn, text) {
    if (!btn) return;
    btn.textContent = text;
    setTimeout(function () { btn.textContent = 'Refresh'; btn.disabled = false; }, 1600);
  }
  function bakeAgeMin() {
    if (!DATA || !DATA.meta || !DATA.meta.baked_at) return null;
    return (Date.now() - new Date(DATA.meta.baked_at).getTime()) / 60000;
  }
  function nextBakeLabel() {
    var age = bakeAgeMin();
    if (age === null) return 'Latest';
    var left = Math.ceil(BAKE_EVERY_MIN - age);
    return left > 0 ? 'Latest · ' + left + 'm' : 'Latest · due';
  }
  function refreshData(manual, attempt) {
    var url = document.body.dataset.vaultDataUrl;
    if (!url) return;
    var btn = $('[data-refresh]');
    attempt = attempt || 0;
    if (manual && btn) { btn.disabled = true; btn.textContent = attempt ? 'Waiting…' : 'Checking…'; }
    fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + '_v=' + Date.now(), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (json) {
        var changed = !DATA || !DATA.meta || json.meta.baked_at !== DATA.meta.baked_at;
        if (!changed) {
          paintSync();
          if (manual) {
            // A bake that is already due may be mid-write. Give it a few
            // seconds rather than reporting "latest" on data we can see is
            // about to be replaced.
            var age = bakeAgeMin();
            if (age !== null && age >= BAKE_EVERY_MIN && attempt < 3) {
              setTimeout(function () { refreshData(true, attempt + 1); }, 3000);
              return;
            }
            flashBtn(btn, nextBakeLabel());
            announce('Already on the latest bake');
          }
          return;
        }
        // background polls never yank the DOM out from under the user's typing
        var ae = document.activeElement;
        if (!manual && ae && /input|textarea/i.test(ae.tagName)) return;
        DATA = json;
        $$('[data-panel]').forEach(function (p) { p.dataset.built = ''; p.textContent = ''; });
        buildPanel(current);
        paintSync();
        if (manual) flashBtn(btn, 'Updated ✓');
        announce('Data refreshed');
      })
      .catch(function () { if (manual) { flashBtn(btn, 'Retry'); announce('Refresh failed'); } });
  }

  /* ── gate ─────────────────────────────────────────────────── */
  function unlock() {
    document.body.dataset.state = 'unlocked';
    var gate = $('[data-gate]'); if (gate) gate.hidden = true;
    var app = $('[data-app]'); if (app) app.hidden = false;
    var m = location.hash.match(/tab=([\w-]+)/);
    activate(m ? m[1] : 'today', false);
    refreshData(false);
    announce('Vault unlocked');
  }
  function lock() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
    location.reload();
  }
  function wireGate() {
    var form = $('[data-gate-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = $('#vault-passphrase');
      var err = $('[data-gate-error]');
      sha256(input.value).then(function (hash) {
        if (hash === window.VAULT_HASH) {
          try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e2) {}
          unlock();
        } else {
          if (err) err.hidden = false;
          input.select();
        }
      });
    });
  }

  /* ── boot ─────────────────────────────────────────────────── */
  function boot() {
    wireGate(); wireTabs();
    var lockBtn = $('[data-lock]'); if (lockBtn) lockBtn.addEventListener('click', lock);
    var refBtn = $('[data-refresh]'); if (refBtn) refBtn.addEventListener('click', function () { refreshData(true); });
    setInterval(paintSync, 60000);
    setInterval(function () {
      if (document.body.dataset.state === 'unlocked') refreshData(false);
    }, 120000);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && document.body.dataset.state === 'unlocked') refreshData(false);
    });
    var unlocked = false;
    try { unlocked = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) {}
    if (unlocked) unlock();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
